import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, Heart, X, ChevronDown, LogOut, LayoutDashboard } from "lucide-react";
import { Logo } from "../Logo";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";

const NAV = [
  { label: "Buy", to: "/buy" },
  { label: "Rent", to: "/rent" },
  { label: "Plots", to: "/plots" },
  { label: "Commercial", to: "/commercial" },
  { label: "New Projects", to: "/projects" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { saved } = useApp();
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="main-header"
      className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-pbs-line">
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-3 transition-all duration-300 ${scrolled ? "h-14" : "h-16"}`}>
        <Logo className="shrink-0" />

        <nav className="hidden xl:flex items-center gap-0.5 min-w-0">
          {NAV.map((n) => (
            <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="px-2.5 py-1.5 text-sm font-semibold text-pbs-dark/80 hover:text-pbs-purple rounded-md transition-colors relative group whitespace-nowrap">
              {n.label}
              <span className="absolute left-2.5 right-2.5 -bottom-0 h-0.5 bg-pbs-orange scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </Link>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2 shrink-0">
          <Link to="/post-property" data-testid="post-property-btn"
            className="whitespace-nowrap px-3 py-1.5 text-sm font-semibold text-pbs-purple border border-pbs-purple/30 rounded-lg hover:bg-pbs-purple/5 transition-colors">
            Post Property <span className="text-pbs-orange">FREE</span>
          </Link>
          <Link to="/saved" data-testid="saved-link" className="relative p-1.5 text-pbs-dark/70 hover:text-pbs-purple transition-colors">
            <Heart size={19} />
            {saved.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-pbs-coral text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{saved.length}</span>}
          </Link>
          {user ? (
            <Link to="/dashboard" data-testid="user-menu-btn" className="flex items-center gap-2 p-1 rounded-lg hover:bg-pbs-bg transition-colors">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-pbs-purple" referrerPolicy="no-referrer" />
              ) : (
                <span className="w-8 h-8 rounded-full pbs-gradient text-white flex items-center justify-center text-sm font-extrabold">{(user.name || user.email)[0].toUpperCase()}</span>
              )}
            </Link>
          ) : (
            <button data-testid="login-btn" onClick={login}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-pbs-dark hover:text-pbs-purple transition-colors">
              <LayoutDashboard size={17} /> Login
            </button>
          )}
          <button data-testid="explore-properties-btn" onClick={() => navigate("/properties")}
            className="whitespace-nowrap px-4 py-2 text-sm font-bold text-white rounded-lg pbs-gradient hover:opacity-95 transition-opacity shadow-sm">
            Explore Properties
          </button>
        </div>

        <div className="flex xl:hidden items-center gap-1 shrink-0">
          <button data-testid="mobile-search-btn" onClick={() => navigate("/properties")} aria-label="Search" className="p-2 text-pbs-dark/70 max-[360px]:hidden"><Search size={20} /></button>
          {user ? (
            <Link to="/dashboard" data-testid="mobile-login-btn" className="p-1.5">
              {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full border-2 border-pbs-purple" referrerPolicy="no-referrer" />
                : <span className="w-8 h-8 rounded-full pbs-gradient text-white flex items-center justify-center text-xs font-extrabold">{(user.name || user.email)[0].toUpperCase()}</span>}
            </Link>
          ) : (
            <button data-testid="mobile-login-btn" onClick={login} className="px-2.5 py-1.5 text-sm font-bold text-pbs-purple whitespace-nowrap">Login</button>
          )}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="p-2 text-pbs-dark"><Menu size={22} /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[82%] sm:w-80 bg-white p-0">
              <div className="p-5 border-b border-pbs-line flex items-center justify-between">
                <Logo />
                <button onClick={() => setMobileOpen(false)}><X size={20} /></button>
              </div>
              <nav className="flex flex-col p-3">
                {NAV.map((n) => (
                  <Link key={n.label} to={n.to} onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="px-4 py-3 text-base font-semibold text-pbs-dark border-b border-pbs-line/60 flex items-center justify-between">
                    {n.label} <ChevronDown size={16} className="-rotate-90 text-pbs-grey" />
                  </Link>
                ))}
                {[["Calculators", "/calculators"], ["Developers", "/developers"], ["Contact Us", "/contact"], ["Careers", "/careers"], ["My Dashboard", "/dashboard"]].map(([l, to]) => (
                  <Link key={l} to={to} onClick={() => setMobileOpen(false)} className="px-4 py-3 text-base font-semibold text-pbs-dark border-b border-pbs-line/60">{l}</Link>
                ))}
                {user ? (
                  <button onClick={() => { logout(); setMobileOpen(false); }} className="mt-4 mx-2 px-4 py-3 flex items-center justify-center gap-2 font-bold text-pbs-coral border border-pbs-line rounded-lg"><LogOut size={16} /> Logout</button>
                ) : (
                  <button onClick={() => { login(); }} className="mt-4 mx-2 px-4 py-3 text-center font-bold text-white rounded-lg pbs-gradient">Login / Sign Up</button>
                )}
                <Link to="/post-property" onClick={() => setMobileOpen(false)} className="mt-3 mx-2 px-4 py-3 text-center font-bold text-pbs-purple border border-pbs-purple/30 rounded-lg">Post Property Free</Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export default Header;
